/**
 * +zone: the Mossvale zone's terrain (docs/design/zones.md "Mossvale"): one deterministic height
 * function and the water shapes, shared by the renderer (ground, units, props, camera) and the
 * content tests. The sim stays 2D: impassable cliffs and deep water are DungeonDef.obstacles
 * (mossvaleZone.ts builds them from the same geometry); walkable slopes are just walkable.
 *
 * Coordinates are sim yards (x east, y north) on an 864 × 810 frame (v2, the 1–10 journey).
 */
import type { Vec2 } from '@keystone/sim';

// ---------------------------------------------------------------------------
// Shared geometry (the zone definition builds obstacles / areas from the same numbers)
// ---------------------------------------------------------------------------

export const ZONE_W = 864;
export const ZONE_H = 810;

/** The crossroads town (safe hub). */
export const TOWN: Vec2 = { x: 430, y: 150 };
export const TOWN_R = 24;

/** The Slime Hollows: meadow dells west of town (level 1–2). */
export const SLIME: Vec2 = { x: 258, y: 112 };

/**
 * Glimmermere: a lake east of town with a noise-wobbled shoreline (lakeShape), a shallow bay in the
 * south-west, an inlet in the north where the river comes in, and Wisp Isle east of its middle,
 * waded to along a sandbar from the west.
 */
export const LAKE = { c: { x: 720, y: 240 }, rx: 72, ry: 58, island: 11, isle: { x: 742, y: 240 }, causewayHalf: 3, level: -0.35 } as const;

/** Shoreline radius factor at polar angle `a` (around LAKE.c, in ellipse space) and how much of a shallow bay it is (0..1). */
export function lakeShape(a: number): { f: number; bay: number } {
  const dA = (t: number): number => Math.atan2(Math.sin(a - t), Math.cos(a - t));
  const bay = Math.exp(-((dA(-2.35) / 0.42) ** 2));
  const inlet = Math.exp(-((dA(Math.PI / 2) / 0.1) ** 2));
  const f = 1 + 0.09 * Math.sin(3 * a + 0.7) + 0.06 * Math.sin(5 * a + 2.1) + 0.035 * Math.sin(9 * a + 4) + 0.2 * bay + 0.22 * inlet;
  return { f, bay };
}
/** Deep water (blocks): the lake outline scaled by this (the band outside it is a wadeable shore). */
export const LAKE_DEEP = 0.84;

/** The river: from Silverstep Falls at the plateau's foot down into the lake's north inlet; a ford where the east road crosses. */
export const RIVER: readonly Vec2[] = [
  { x: 709, y: 540 },
  { x: 708, y: 524 },
  { x: 716, y: 494 },
  { x: 704, y: 462 },
  { x: 714, y: 420 },
  { x: 706, y: 370 },
  { x: 716, y: 300 },
];
export const RIVER_HALF = 3;
/** The ford (walkable, shallow) where the foothill road crosses the river. */
export const FORD = { y0: 404, y1: 418 } as const;
/** The waterfall at the plateau's lip. */
export const FALLS = { x: 708.5, y: 526, top: 26.5 } as const;

/** Greyhorn Heights: a plateau north-east (height PLATEAU_H), cliffs on its south / west faces. */
export const PLATEAU_H = 26;
/** Plateau rim: y above this (south face), x above PLATEAU_X0 (west face, below the saddle). */
export const PLATEAU_Y0 = 520;
export const PLATEAU_X0 = 610;
/** The saddle north of this y connects the plateau to the Pale Rise (gentle, walkable). */
export const SADDLE_Y = 712;
/** Goat's Climb: the ramp up the south face. */
export const RAMP = { x0: 628, x1: 646, y0: 438, y1: 530 } as const;

/** The Pale Rise: a broad, proper hill north of the Barrow, ruins on top, the lookout at its summit. */
export const HILL = { c: { x: 440, y: 650 }, r: 135, h: 40 } as const;

/** The crags behind Tallowdeep Mine (north-east corner). */
export const CRAGS: Vec2 = { x: 840, y: 790 };

/**
 * +detail: the Hollowcrown, a giant ancient oak on a rise north-west of town: the landmark seen from
 * the square and from the Slime Hollows (its knoll lifts it over the town roofs).
 */
export const ELDER_TREE: Vec2 = { x: 338, y: 244 };
/** +detail: the Old Beacon, a ruined signal tower on a hillock south-east of town (the east road passes it). */
export const BEACON: Vec2 = { x: 496, y: 118 };
/** +detail: low rises under the quiet country's landmarks (centre, radius, height in yd). */
export const KNOLLS: readonly { c: Vec2; r: number; h: number }[] = [
  { c: ELDER_TREE, r: 52, h: 7 },
  { c: BEACON, r: 30, h: 4 },
];

/**
 * +spawn: Granny Pell's cottage on a gentle rise at the southern edge of the Slime Hollows (new
 * heroes start here, looking north across the dells toward the Hollowcrown, the town and the Tower).
 */
export const PELL_HOME: Vec2 = { x: 214, y: 32 };

/**
 * +elev: ridges between the areas (centre lines, crest height, half-width). They separate the
 * hunting grounds; roads cross them through cuts (registerTerrainRoads).
 */
export const RIDGE_LINES: readonly { pts: readonly Vec2[]; h: number; w: number }[] = [
  // Between the Slime Hollows and the Boar Run.
  { pts: [{ x: 110, y: 190 }, { x: 190, y: 200 }, { x: 250, y: 240 }], h: 10, w: 26 },
  // North of town: the long ridge under Barrowfield (the north road cuts through it).
  { pts: [{ x: 300, y: 300 }, { x: 380, y: 285 }, { x: 470, y: 300 }, { x: 560, y: 290 }], h: 11, w: 24 },
  // Between Barrowfield and Bramblewood.
  { pts: [{ x: 300, y: 430 }, { x: 320, y: 500 }, { x: 350, y: 560 }], h: 9, w: 22 },
  // Between town and the lake country.
  { pts: [{ x: 540, y: 60 }, { x: 560, y: 140 }, { x: 545, y: 220 }], h: 9, w: 24 },
  // Between Barrowfield and the outpost / the valley road.
  { pts: [{ x: 540, y: 400 }, { x: 575, y: 470 }, { x: 590, y: 540 }], h: 10, w: 22 },
];

/**
 * +ore: rocky outcrops (small knolls crowned with boulders) where the low-level ore grows: the
 * Hollows, the farm country, round town, Barrowfield, the lake's south shore, the foothills. The
 * zone stands boulders on them (mossvaleZone.ts) and the mining nodes sit at their feet.
 */
export const OUTCROPS: readonly Vec2[] = [
  { x: 196, y: 92 },
  { x: 262, y: 58 },
  { x: 300, y: 82 },
  { x: 452, y: 224 },
  { x: 404, y: 252 },
  { x: 196, y: 302 },
  { x: 168, y: 324 },
  { x: 54, y: 372 },
  { x: 80, y: 398 },
  { x: 330, y: 44 },
  { x: 362, y: 58 },
  { x: 470, y: 470 },
  { x: 440, y: 478 },
  { x: 650, y: 150 },
  { x: 690, y: 140 },
  { x: 614, y: 398 },
  { x: 688, y: 336 },
  { x: 566, y: 314 },
  { x: 660, y: 446 },
  { x: 284, y: 492 },
  { x: 226, y: 552 },
  { x: 720, y: 600 },
  { x: 756, y: 628 },
  { x: 700, y: 642 },
  { x: 790, y: 440 },
  { x: 128, y: 636 },
  { x: 520, y: 182 },
  { x: 402, y: 180 },
  { x: 676, y: 582 },
  { x: 772, y: 688 },
  { x: 248, y: 580 },
  { x: 482, y: 440 },
  { x: 712, y: 318 },
];

// ---------------------------------------------------------------------------
// +elev: roads are cut into the slopes (the zone registers its road lines once)
// ---------------------------------------------------------------------------

const ROAD_CELL = 2;
const ROAD_REACH = 22;
let roadGrid: Float32Array | null = null;
let roadLines: readonly (readonly Vec2[])[] = [];
const RG_X0 = -80;
const RG_Y0 = -80;
const RG_NX = Math.ceil((ZONE_W + 160) / ROAD_CELL) + 1;
const RG_NY = Math.ceil((ZONE_H + 160) / ROAD_CELL) + 1;

/** The zone's road and trail centre lines (mossvaleZone.ts calls this once at import). */
export function registerTerrainRoads(lines: readonly (readonly Vec2[])[]): void {
  roadLines = lines;
  roadGrid = null;
}

/** Distance (yd, capped at ROAD_REACH) from the nearest road, bilinear on a 2 yd grid built lazily. */
function roadDist(x: number, y: number): number {
  if (!roadLines.length) return ROAD_REACH;
  if (!roadGrid) {
    const g = new Float32Array(RG_NX * RG_NY).fill(ROAD_REACH);
    for (const l of roadLines)
      for (let i = 0; i < l.length - 1; i++) {
        const a = l[i]!;
        const b = l[i + 1]!;
        const i0 = Math.max(0, Math.floor((Math.min(a.x, b.x) - ROAD_REACH - RG_X0) / ROAD_CELL));
        const i1 = Math.min(RG_NX - 1, Math.ceil((Math.max(a.x, b.x) + ROAD_REACH - RG_X0) / ROAD_CELL));
        const j0 = Math.max(0, Math.floor((Math.min(a.y, b.y) - ROAD_REACH - RG_Y0) / ROAD_CELL));
        const j1 = Math.min(RG_NY - 1, Math.ceil((Math.max(a.y, b.y) + ROAD_REACH - RG_Y0) / ROAD_CELL));
        for (let j = j0; j <= j1; j++)
          for (let k = i0; k <= i1; k++) {
            const d = distToPolyline({ x: RG_X0 + k * ROAD_CELL, y: RG_Y0 + j * ROAD_CELL }, [a, b]);
            const at = j * RG_NX + k;
            if (d < g[at]!) g[at] = d;
          }
      }
    roadGrid = g;
  }
  const fx = (x - RG_X0) / ROAD_CELL;
  const fy = (y - RG_Y0) / ROAD_CELL;
  const i = Math.floor(fx);
  const j = Math.floor(fy);
  if (i < 0 || j < 0 || i >= RG_NX - 1 || j >= RG_NY - 1) return ROAD_REACH;
  const tx = fx - i;
  const ty = fy - j;
  const g = roadGrid;
  const a = g[j * RG_NX + i]!;
  const b = g[j * RG_NX + i + 1]!;
  const c = g[(j + 1) * RG_NX + i]!;
  const d = g[(j + 1) * RG_NX + i + 1]!;
  return a + (b - a) * tx + (c - a) * ty + (a - b - c + d) * tx * ty;
}

// ---------------------------------------------------------------------------
// Noise
// ---------------------------------------------------------------------------

function hash2(x: number, y: number): number {
  const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return s - Math.floor(s);
}
function vnoise(x: number, y: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const u = xf * xf * (3 - 2 * xf);
  const v = yf * yf * (3 - 2 * yf);
  const a = hash2(xi, yi);
  const b = hash2(xi + 1, yi);
  const c = hash2(xi, yi + 1);
  const e = hash2(xi + 1, yi + 1);
  return a + (b - a) * u + (c - a) * v + (a - b - c + e) * u * v;
}
const smooth = (e0: number, e1: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

export function distToPolyline(p: Vec2, pts: readonly Vec2[]): number {
  let best = Infinity;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i]!;
    const b = pts[i + 1]!;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const l2 = dx * dx + dy * dy;
    const t = l2 === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2));
    best = Math.min(best, Math.hypot(p.x - (a.x + dx * t), p.y - (a.y + dy * t)));
  }
  return best;
}

/** Lake value: < 1 inside the lake's (wobbly) outline. */
export function lakeE(x: number, y: number): number {
  const dx = (x - LAKE.c.x) / LAKE.rx;
  const dy = (y - LAKE.c.y) / LAKE.ry;
  const f = lakeShape(Math.atan2(dy, dx)).f;
  return (dx * dx + dy * dy) / (f * f);
}

/**
 * The plateau's south rim (y of the cliff top) at x: it wanders (bluffs and bays), level at Goat's
 * Climb and at the falls so the ramp and the falls meet a straight lip.
 */
export function plateauRimY(x: number): number {
  const calm = smooth(10, 26, Math.abs(x - (RAMP.x0 + RAMP.x1) / 2)) * smooth(8, 20, Math.abs(x - FALLS.x));
  return PLATEAU_Y0 + calm * (7 * Math.sin(x * 0.031 + 0.6) + 4 * Math.sin(x * 0.083 + 2.1));
}
/** The plateau's west rim (x of the cliff top) at y (below the saddle). */
export function plateauRimX(y: number): number {
  return PLATEAU_X0 + smooth(PLATEAU_Y0 + 6, PLATEAU_Y0 + 24, y) * (6 * Math.sin(y * 0.037 + 1.9) + 3.5 * Math.sin(y * 0.091));
}

/** 0 on the low ground .. 1 on the plateau (sharp at the cliffs, a gentle saddle in the north, the ramp in the south). */
export function plateauK(x: number, y: number): number {
  const ry = plateauRimY(x);
  const rx = plateauRimX(y);
  const south = smooth(ry - 12, ry + 1, y);
  const westHard = smooth(rx - 10, rx + 1, x);
  const westSoft = smooth(520, 640, x);
  const t = smooth(SADDLE_Y - 14, SADDLE_Y + 10, y);
  let k = south * lerp(westHard, westSoft, t);
  // Goat's Climb: a straight ramp up the south face.
  if (x > RAMP.x0 - 5 && x < RAMP.x1 + 5 && y > RAMP.y0 && y < RAMP.y1 + 2) {
    const across = smooth(RAMP.x0 - 5, RAMP.x0 + 1, x) * (1 - smooth(RAMP.x1 - 1, RAMP.x1 + 5, x));
    const up = smooth(RAMP.y0, RAMP.y1, y);
    k = lerp(k, Math.max(k, up), across);
  }
  return k;
}

// ---------------------------------------------------------------------------
// Height
// ---------------------------------------------------------------------------

/**
 * Terrain height (yards) at sim (x, y): rolling meadows, the flat town, the Pale Rise, the
 * Greyhorn plateau and its ramp, Bramblewood's hummocks, the lake basin and the river cut.
 * Deterministic and pure (no state): renderer and tests agree.
 */
export function mossvaleHeight(x: number, y: number): number {
  // +elev: roads are cut into the slopes: the relief (not the big landforms) flattens toward them.
  const cut = 1 - 0.8 * smooth(ROAD_REACH - 2, 4, roadDist(x, y));
  // Rolling ground: broad swells with real relief, hills and knolls; valleys between.
  const swell = 15 * (vnoise(x * 0.0065 + 3.1, y * 0.0065 - 1.7) - 0.45) + 6 * (vnoise(x * 0.017 - 5, y * 0.017 + 9) - 0.5);
  let h = swell * (0.55 + 0.45 * cut) + 1.2 * cut * (vnoise(x * 0.06 + 2, y * 0.06) - 0.5);
  // Terraces on the broad slopes (soft steps), strongest in the open country north of town.
  const terr = smooth(0.45, 0.75, vnoise(x * 0.004 + 21, y * 0.004 - 6));
  if (terr > 0) {
    const step = 3.2;
    const q = Math.floor(h / step) * step;
    const f = (h - q) / step;
    h = lerp(h, q + step * smooth(0.55, 0.95, f), 0.65 * terr * cut);
  }
  // Ridges between the areas (crests ragged with noise), cut where a road crosses.
  for (const r of RIDGE_LINES) {
    const dr = distToPolyline({ x, y }, r.pts);
    if (dr < r.w) h += r.h * (0.75 + 0.5 * vnoise(x * 0.03 + r.h, y * 0.03)) * smooth(r.w, 0, dr) ** 1.3 * cut;
  }
  // The crags behind Tallowdeep Mine.
  h += 26 * smooth(90, 0, Math.hypot(x - CRAGS.x, y - CRAGS.y)) ** 1.4;
  // Bramblewood (north-west): hummocks and hollows, climbing toward the Thornheart.
  const west = smooth(340, 220, x) * smooth(400, 500, y);
  h += west * (8 * (vnoise(x * 0.02 + 11, y * 0.02 + 2) - 0.35) * cut + 7 * smooth(480, 760, y));
  // The Slime Hollows: soft dells and knolls.
  const ds = Math.hypot(x - SLIME.x, y - SLIME.y);
  if (ds < 110) h += smooth(110, 50, ds) * 3.4 * (Math.sin(x * 0.085 + 1.3) * Math.cos(y * 0.075 - 0.4)) * cut;
  // +spawn: Granny Pell's rise at the Hollows' southern edge (the hero's first view, north over the dells).
  const dp = Math.hypot(x - PELL_HOME.x, y - PELL_HOME.y);
  if (dp < 70) h = lerp(h, 6.5 + 0.6 * (vnoise(x * 0.1, y * 0.1) - 0.5), smooth(70, 18, dp));
  // Brookside Farm sits on a gentle rise.
  h += 3.5 * smooth(150, 40, Math.hypot(x - 140, y - 270));
  // +detail: the knolls under the Hollowcrown and the Old Beacon.
  for (const k of KNOLLS) {
    const dk = Math.hypot(x - k.c.x, y - k.c.y);
    if (dk < k.r) h += k.h * 1.4 * smooth(k.r, k.r * 0.2, dk);
  }
  // +ore: the rocky knolls.
  for (const o of OUTCROPS) {
    const dk = Math.hypot(x - o.x, y - o.y);
    if (dk < 14) h += 2.6 * smooth(14, 3, dk);
  }
  // The Pale Rise: a proper hill.
  const dh = Math.hypot(x - HILL.c.x, y - HILL.c.y) / HILL.r;
  if (dh < 1) h += HILL.h * Math.pow(0.5 + 0.5 * Math.cos(dh * Math.PI), 1.15);
  // Greyhorn Heights: the plateau (with relief on top), steeper and taller.
  const pk = plateauK(x, y);
  if (pk > 0) h = lerp(h, PLATEAU_H + 5 * (vnoise(x * 0.03 + 40, y * 0.03) - 0.5), pk);
  // The town square is flat.
  const dt = Math.hypot(x - TOWN.x, y - TOWN.y);
  h = lerp(h, 0.5, 1 - smooth(TOWN_R - 2, TOWN_R + 26, dt));
  // Hills around the lake basin (behind the shore).
  const e = lakeE(x, y);
  if (e > 1.4 && e < 4.5) h += 7 * smooth(1.4, 2.4, e) * smooth(4.5, 2.8, e) * (0.6 + 0.8 * vnoise(x * 0.025 + 5, y * 0.025)) * cut * smooth(20, 60, Math.abs(x - 712));
  // The river's valley: a gorge stepping down from the falls to the lake.
  if (y < FALLS.y + 2) {
    const dr = distToPolyline({ x, y }, RIVER);
    if (dr < 30) {
      const prof = lerp(LAKE.level + 0.6, 2.4, smooth(300, 520, y));
      h = lerp(h, Math.min(h, prof + 2.5 * smooth(6, 24, dr)), smooth(30, 8, dr));
    }
  }
  // Glimmermere: the basin, the island, the causeway.
  if (e < 1.35) {
    const bay = lakeShape(Math.atan2((y - LAKE.c.y) / LAKE.ry, (x - LAKE.c.x) / LAKE.rx)).bay;
    const shore = smooth(1.35, 0.98, e);
    const deep = smooth(0.98, 0.45, e) * (1 - 0.75 * bay);
    h = lerp(h, LAKE.level + 0.1, shore) - 3.2 * deep - 0.25 * bay * smooth(1.0, 0.7, e);
    const di = Math.hypot(x - LAKE.isle.x, y - LAKE.isle.y);
    h = Math.max(h, lerp(LAKE.level - 1.2, 1.4, smooth(LAKE.island + 3, LAKE.island - 5, di)));
    if (Math.abs(y - LAKE.isle.y) < LAKE.causewayHalf + 2 && x < LAKE.isle.x) h = Math.max(h, lerp(-2, LAKE.level - 0.12, smooth(LAKE.causewayHalf + 2, LAKE.causewayHalf - 0.5, Math.abs(y - LAKE.isle.y))));
  }
  // Around the lake the ground stays above the water (the water sheet covers the basin's square).
  if (e >= 0.98 && e < 2.6) h = Math.max(h, LAKE.level + 0.35 * smooth(0.98, 1.2, e));
  // The river cut (shallow at the ford), below the falls.
  if (y < FALLS.y + 2) {
    const dr = distToPolyline({ x, y }, RIVER);
    if (dr < 9) {
      const ford = y > FORD.y0 && y < FORD.y1 ? 0.25 : 1;
      h -= 2.2 * ford * (1 - smooth(RIVER_HALF - 0.5, RIVER_HALF + 4.5, dr));
    }
  }
  return h;
}

/** Water surfaces for the renderer: the lake (ellipse) and the river (a polyline strip). */
export const MOSSVALE_WATER = {
  level: LAKE.level,
  lake: LAKE,
  river: RIVER,
  riverHalf: RIVER_HALF + 1.2,
  falls: FALLS,
} as const;

/**
 * +glide: the floor a flyer lands on (the sim's AirspaceDef.floorAt): the terrain, or the water
 * surface over the lake and the river below the falls.
 */
export function mossvaleFloor(x: number, y: number): number {
  const h = mossvaleHeight(x, y);
  if (h >= LAKE.level) return h;
  if (lakeE(x, y) < 1.05 || (y < FALLS.y + 2 && distToPolyline({ x, y }, RIVER) < RIVER_HALF + 1.2)) return LAKE.level;
  return h;
}
